import { useState } from "react";
import { ArrowLeft, Package, Plus } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/select";

const mockProducts: Record<
  string,
  {
    product_name: string;
    quantity: string;
    brand: string;
    calories: number;
    protein: number;
    carbohydrates: number;
    sugar: number;
    fat: number;
    saturated_fat: number;
    trans_fat: number;
    fiber: number;
    sodium: number;
    cholesterol: number;
    alcohol: number;
    caffeine: number;
  }
> = {
  "3017620422003": {
    product_name: "Nutella Hazelnut Spread",
    quantity: "400g",
    brand: "Ferrero",
    calories: 539,
    protein: 6.3,
    carbohydrates: 57.5,
    sugar: 56.3,
    fat: 30.9,
    saturated_fat: 10.6,
    trans_fat: 0,
    fiber: 3.4,
    sodium: 41,
    cholesterol: 0,
    alcohol: 0,
    caffeine: 0,
  },
  "5449000000996": {
    product_name: "Coca-Cola Original",
    quantity: "330ml",
    brand: "The Coca-Cola Company",
    calories: 139,
    protein: 0,
    carbohydrates: 35,
    sugar: 35,
    fat: 0,
    saturated_fat: 0,
    trans_fat: 0,
    fiber: 0,
    sodium: 10,
    cholesterol: 0,
    alcohol: 0,
    caffeine: 32,
  },
  "7622210449283": {
    product_name: "Oreo Original Cookies",
    quantity: "154g",
    brand: "Mondelez",
    calories: 480,
    protein: 4.6,
    carbohydrates: 69,
    sugar: 36,
    fat: 20,
    saturated_fat: 9.2,
    trans_fat: 0,
    fiber: 2.2,
    sodium: 390,
    cholesterol: 0,
    alcohol: 0,
    caffeine: 5,
  },
};

const fallbackProduct = {
  product_name: "Unknown Product",
  quantity: "N/A",
  brand: "Unknown",
  calories: 0,
  protein: 0,
  carbohydrates: 0,
  sugar: 0,
  fat: 0,
  saturated_fat: 0,
  trans_fat: 0,
  fiber: 0,
  sodium: 0,
  cholesterol: 0,
  alcohol: 0,
  caffeine: 0,
};

const NutrientCell = ({
  label,
  value,
  unit,
  color,
}: {
  label: string;
  value: number;
  unit: string;
  color: string;
}) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-xl font-bold ${color}`}>
      {value}
      {unit}
    </p>
  </div>
);

const BarcodeResult = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code") || "";
  const product = mockProducts[code] || fallbackProduct;
  const isUnknown = !mockProducts[code];
  const [mealType, setMealType] = useState<string>("");

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="px-4 pb-4 pt-8">
        <div className="mb-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Product Info</h1>
        </div>

        {/* Product Name Card */}
        <Card className="flex items-center gap-3 p-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Package className="h-6 w-6 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold leading-tight text-foreground">
              {product.product_name}
            </h2>
            <p className="text-xs text-muted-foreground">{product.brand}</p>
          </div>
          <Badge variant="secondary" className="flex-shrink-0">
            {product.quantity}
          </Badge>
        </Card>
      </div>

      <div className="space-y-4 px-4">
        {isUnknown && (
          <Card className="border-destructive/30 bg-destructive/10 p-3">
            <p className="text-sm font-medium text-foreground">
              Product not found for "{code}"
            </p>
          </Card>
        )}

        {/* Total Nutrition - 2 column grid like the reference */}
        <Card className="p-5">
          <h3 className="mb-4 text-lg font-bold text-foreground">
            Macronutrients
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <NutrientCell
              label="Calories"
              value={product.calories}
              unit=""
              color="text-foreground"
            />
            <NutrientCell
              label="Protein"
              value={product.protein}
              unit="g"
              color="text-green-600"
            />
            <NutrientCell
              label="Carbs"
              value={product.carbohydrates}
              unit="g"
              color="text-orange-500"
            />
            <NutrientCell
              label="Fat"
              value={product.fat}
              unit="g"
              color="text-foreground"
            />
            <NutrientCell
              label="Sugar"
              value={product.sugar}
              unit="g"
              color="text-red-500"
            />
            <NutrientCell
              label="Fiber"
              value={product.fiber}
              unit="g"
              color="text-green-600"
            />
            <NutrientCell
              label="Saturated Fat"
              value={product.saturated_fat}
              unit="g"
              color="text-amber-600"
            />
            <NutrientCell
              label="Trans Fat"
              value={product.trans_fat}
              unit="g"
              color="text-red-600"
            />
          </div>
        </Card>

        {/* Other Nutrients */}
        <Card className="p-5">
          <h3 className="mb-4 text-lg font-bold text-foreground">Other</h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <NutrientCell
              label="Sodium"
              value={product.sodium}
              unit="mg"
              color="text-purple-500"
            />
            <NutrientCell
              label="Cholesterol"
              value={product.cholesterol}
              unit="mg"
              color="text-foreground"
            />
            <NutrientCell
              label="Alcohol"
              value={product.alcohol}
              unit="g"
              color="text-foreground"
            />
            <NutrientCell
              label="Caffeine"
              value={product.caffeine}
              unit="mg"
              color="text-amber-700"
            />
          </div>
        </Card>

        {/* Meal Type Selector */}
        <Card className="p-5">
          <h3 className="mb-2 text-sm font-medium text-foreground">Add to</h3>
          <Select value={mealType} onValueChange={setMealType}>
            <SelectTrigger className="w-full bg-background">
              <SelectValue placeholder="Select meal type" />
            </SelectTrigger>
            <SelectContent className="z-50 bg-background">
              <SelectItem value="breakfast">Breakfast</SelectItem>
              <SelectItem value="lunch">Lunch</SelectItem>
              <SelectItem value="dinner">Dinner</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </Card>

        {/* Add to Meal */}
        <Button
          className="w-full gap-2"
          size="lg"
          disabled={!mealType}
          onClick={() => navigate("/meals")}
        >
          <Plus className="h-5 w-5" />
          Add to Meal
        </Button>
      </div>
    </div>
  );
};

export default BarcodeResult;
